import React from 'react';
import { X, Filter } from 'lucide-react';

const FilterPanel = ({ filters, onFilterChange, onClose }) => {
  const jobTypes = [
    { value: 'full_time', label: 'Full Time' },
    { value: 'part_time', label: 'Part Time' },
    { value: 'contract', label: 'Contract' },
    { value: 'internship', label: 'Internship' },
    { value: 'apprenticeship', label: 'Apprenticeship' },
    { value: 'volunteer', label: 'Volunteer' },
  ];

  const experienceLevels = [
    { value: 'entry_level', label: 'Entry Level' },
    { value: 'junior', label: 'Junior' },
    { value: 'mid_level', label: 'Mid Level' },
    { value: 'senior_level', label: 'Senior Level' },
    { value: 'executive', label: 'Executive' },
    { value: 'student', label: 'Student' },
  ];

  const categories = [
    { value: 'technology', label: 'Technology' },
    { value: 'transportation', label: 'Transportation' },
    { value: 'energy', label: 'Energy' },
    { value: 'water_management', label: 'Water Management' },
    { value: 'waste_management', label: 'Waste Management' },
    { value: 'governance', label: 'Governance' },
    { value: 'education', label: 'Education' },
    { value: 'healthcare', label: 'Healthcare' },
    { value: 'safety_security', label: 'Safety & Security' },
    { value: 'economic_development', label: 'Economic Development' },
  ];

  const handleFilterChange = (key, value) => {
    onFilterChange({
      ...filters,
      [key]: filters?.[key] === value ? null : value,
    });
  };

  const clearAllFilters = () => {
    onFilterChange({
      category: null,
      jobType: null,
      experienceLevel: null,
      location: null,
      isRemote: null,
    });
  };

  const activeFilterCount = Object.values(filters || {})?.filter(Boolean)?.length || 0;

  return (
    <div className="bg-white rounded-lg border shadow-sm p-6">
      {/* Header */}
      <div className="flex items-center justify-between mb-6">
        <div className="flex items-center gap-2">
          <Filter className="w-5 h-5 text-gray-700" />
          <h3 className="text-lg font-semibold text-gray-900">Filters</h3>
          {activeFilterCount > 0 && (
            <span className="bg-blue-100 text-blue-800 text-xs font-medium px-2 py-1 rounded-full">
              {activeFilterCount}
            </span>
          )}
        </div>
        <button
          onClick={onClose}
          className="p-2 hover:bg-gray-100 rounded-lg transition-colors lg:hidden"
        >
          <X className="w-4 h-4" />
        </button>
      </div>

      {/* Clear All Filters */}
      {activeFilterCount > 0 && (
        <button
          onClick={clearAllFilters}
          className="w-full text-sm text-blue-600 hover:text-blue-800 mb-6 text-left"
        >
          Clear all filters
        </button>
      )}

      <div className="space-y-6">
        {/* Job Type */}
        <div>
          <h4 className="text-sm font-medium text-gray-900 mb-3">Job Type</h4>
          <div className="space-y-2">
            {jobTypes?.map((type) => (
              <label key={type?.value} className="flex items-center">
                <input
                  type="checkbox"
                  checked={filters?.jobType === type?.value}
                  onChange={() => handleFilterChange('jobType', type?.value)}
                  className="h-4 w-4 text-blue-600 border-gray-300 rounded focus:ring-blue-500"
                />
                <span className="ml-2 text-sm text-gray-700">{type?.label}</span>
              </label>
            ))}
          </div>
        </div>

        {/* Experience Level */}
        <div>
          <h4 className="text-sm font-medium text-gray-900 mb-3">Experience Level</h4>
          <div className="space-y-2">
            {experienceLevels?.map((level) => (
              <label key={level?.value} className="flex items-center">
                <input
                  type="checkbox"
                  checked={filters?.experienceLevel === level?.value}
                  onChange={() => handleFilterChange('experienceLevel', level?.value)}
                  className="h-4 w-4 text-blue-600 border-gray-300 rounded focus:ring-blue-500"
                />
                <span className="ml-2 text-sm text-gray-700">{level?.label}</span>
              </label>
            ))}
          </div>
        </div>

        {/* Category */}
        <div>
          <h4 className="text-sm font-medium text-gray-900 mb-3">Category</h4>
          <div className="space-y-2 max-h-48 overflow-y-auto">
            {categories?.map((category) => (
              <label key={category?.value} className="flex items-center">
                <input
                  type="checkbox"
                  checked={filters?.category === category?.value}
                  onChange={() => handleFilterChange('category', category?.value)}
                  className="h-4 w-4 text-blue-600 border-gray-300 rounded focus:ring-blue-500"
                />
                <span className="ml-2 text-sm text-gray-700">{category?.label}</span>
              </label>
            ))}
          </div>
        </div>

        {/* Remote Work */}
        <div>
          <h4 className="text-sm font-medium text-gray-900 mb-3">Work Type</h4>
          <div className="space-y-2">
            <label className="flex items-center">
              <input
                type="checkbox"
                checked={filters?.isRemote === true}
                onChange={() => handleFilterChange('isRemote', true)}
                className="h-4 w-4 text-blue-600 border-gray-300 rounded focus:ring-blue-500"
              />
              <span className="ml-2 text-sm text-gray-700">Remote</span>
            </label>
            <label className="flex items-center">
              <input
                type="checkbox"
                checked={filters?.isRemote === false}
                onChange={() => handleFilterChange('isRemote', false)}
                className="h-4 w-4 text-blue-600 border-gray-300 rounded focus:ring-blue-500"
              />
              <span className="ml-2 text-sm text-gray-700">On-site</span>
            </label>
          </div>
        </div>

        {/* Location Search */}
        <div>
          <h4 className="text-sm font-medium text-gray-900 mb-3">Location</h4>
          <input
            type="text"
            placeholder="Enter city or state..."
            value={filters?.location || ''}
            onChange={(e) => handleFilterChange('location', e?.target?.value || null)}
            className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent text-sm"
          />
        </div>
      </div>

      {/* Apply Filters Button (Mobile) */}
      <div className="mt-6 lg:hidden">
        <button
          onClick={onClose}
          className="w-full px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 font-medium"
        >
          Apply Filters
        </button>
      </div>
    </div>
  );
};

export default FilterPanel;